import React, { ReactNode, useCallback } from "react";

import { faBook } from "@fortawesome/pro-solid-svg-icons/faBook";
import { faListRadio } from "@fortawesome/pro-solid-svg-icons/faListRadio";
import { faUsers } from "@fortawesome/pro-solid-svg-icons/faUsers";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import Radio from "antd/es/radio";
import { FormattedMessage, useIntl } from "react-intl";
import { useHistory } from "react-router-dom";

import RadioGroup from "@app/component/input/RadioGroup";
import Path from "@app/enum/Path";
import useAnalytics from "@app/util/contexts/useAnalytics";

import ViewMode from "../enums/ViewMode";

import styles from "./Subheader.module.scss";

interface Props {
  showModeSwitch?: boolean;
  title?: ReactNode;
  totalFilteredRecords?: number;
  totalRecords?: number;
  viewMode: ViewMode;
}

const Subheader: React.FC<Props> = ({
  showModeSwitch = true,
  title,
  totalFilteredRecords,
  totalRecords,
  viewMode,
}) => {
  const analyticIssuer = useAnalytics();
  const intl = useIntl();
  const history = useHistory();

  const changeViewMode = useCallback(
    (newViewMode: ViewMode) => {
      history.push(`${Path.ONBOARDING}/${newViewMode}`);
      if (newViewMode === ViewMode.GUIDES) {
        analyticIssuer.clicked(["Guides View"]);
      } else if (newViewMode === ViewMode.USERS) {
        analyticIssuer.clicked(["Users View"]);
      } else {
        analyticIssuer.clicked(["My guides view"]);
      }
    },
    [analyticIssuer, history]
  );

  return (
    <section className={styles.container}>
      <div className={styles.headingSection}>
        <div className={styles.heading}>
          <h3 className={styles.title}>
            {title ??
              intl.formatMessage({
                id: "scene.onboarding.title",
                defaultMessage: "User Training",
                description: "A title on Onboarding page",
              })}
          </h3>
          <span className={styles.count}>
            <FormattedMessage
              id="scene.onboarding.count"
              defaultMessage="{totalFilteredRecords} / {totalRecords} {viewMode, select, guides {guides} my {guides} other {users}} shown"
              description="Filtered count vs total count message"
              values={{
                totalFilteredRecords: intl.formatNumber(totalFilteredRecords ?? 0),
                totalRecords: intl.formatNumber(totalRecords ?? 0),
                viewMode,
              }}
            />
          </span>
        </div>
        {showModeSwitch && (
          <RadioGroup onChange={changeViewMode} value={viewMode}>
            <Radio.Button key={ViewMode.GUIDES} value={ViewMode.GUIDES}>
              <FontAwesomeIcon className={styles.switchIcon} icon={faBook} size="sm" />
              {intl.formatMessage({
                id: "scene.onboarding.header.viewSwitch.guides",
                defaultMessage: "Guides",
                description: "Guides view button title in view Switch on Onboarding scenes",
              })}
            </Radio.Button>
            <Radio.Button key={ViewMode.USERS} value={ViewMode.USERS}>
              <FontAwesomeIcon className={styles.switchIcon} icon={faUsers} size="sm" />
              {intl.formatMessage({
                id: "scene.onboarding.header.viewSwitch.users",
                defaultMessage: "Users",
                description: "Users view button title in view Switch on Onboarding scenes",
              })}
            </Radio.Button>
            <Radio.Button key={ViewMode.MY} value={ViewMode.MY}>
              <FontAwesomeIcon className={styles.switchIcon} icon={faListRadio} size="sm" />
              {intl.formatMessage({
                id: "scene.onboarding.header.viewSwitch.my",
                defaultMessage: "Assigned to Me",
                description:
                  "Guides Assigned to Me view button title in view Switch on Onboarding scenes",
              })}
            </Radio.Button>
          </RadioGroup>
        )}
      </div>
    </section>
  );
};

export default Subheader;
